import type { ReportMeta, ReportResult } from "@wms/domain";

/** One footer row of a report table: its label and the value of each column (absent — an empty cell). */
export interface FooterRow {
  key: string;
  label: string;
  values: Record<string, unknown>;
}

/**
 * CW31-B02: the footer of a report never adds pieces and kilograms. With one unit (or a report without units) it is
 * the single «Итого» row as before; with several units each unit gets its own «Итого, шт» / «Итого, кг» row for the
 * columns measured in the unit of a row, the other summed columns stay on the first row.
 * FULL0211-B03: a report whose rows add up several products («Выработка исполнителей», «Заполненность мест хранения»)
 * has no unit column — its single row is «Итого, кг», so the sum is never shown without its unit. The server builds
 * the same rows for the Excel export (`reports/registry.py: footer_rows`).
 */
export function footerRows(meta: Pick<ReportMeta, "summary" | "unit_column" | "unit_summary">, data: Pick<ReportResult, "totals" | "totals_by_unit">): FooterRow[] {
  if (!meta.summary.length) return [];
  const byUnit = data.totals_by_unit ?? [];
  const unitSummary = meta.unit_summary ?? [];
  const plain: Record<string, unknown> = {};
  for (const name of meta.summary) plain[name] = data.totals[name];
  if (byUnit.length > 1 && unitSummary.length) {
    return byUnit.map((u, index) => {
      const values: Record<string, unknown> = index === 0 ? Object.fromEntries(Object.entries(plain).filter(([name]) => !unitSummary.includes(name))) : {};
      for (const name of unitSummary) values[name] = u.totals[name];
      if (meta.unit_column) values[meta.unit_column] = u.unit;
      return { key: `unit-${u.unit}`, label: `Итого, ${u.unit}`, values };
    });
  }
  if (meta.unit_column && byUnit.length === 1) plain[meta.unit_column] = byUnit[0].unit;
  const label = !meta.unit_column && unitSummary.length && byUnit.length === 1 && byUnit[0].unit ? `Итого, ${byUnit[0].unit}` : "Итого";
  return [{ key: "total", label, values: plain }];
}
